// src/slices/userSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ORDER_LIST_MY_RESET } from '../constants/orderConstants';
import { clearAxiosAuthorization, getAxiosConfig } from '../utils/http';

// Async Thunks

//delete user
export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (payload, { getState, rejectWithValue }) => {
    // Accepts either a bare id (legacy call shape) or { id, stepUpToken }.
    const { id, stepUpToken } = typeof payload === 'object' && payload !== null ? payload : { id: payload };
    try {
  const state = getState();
  const config = getAxiosConfig(state);
  if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
  await axios.delete(`/api/users/${id}`, config);

      // Optionally you can return id to remove it from local state if you want
      return id;
    } catch (error) {
      const message =
        error.response?.data?.message || error.message;

      return rejectWithValue(message);
    }
  }
);

// List Users (admin)
export const listUsers = createAsyncThunk(
  'user/listUsers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      const { data } = await axios.get('/api/users', config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// User Login
export const login = createAsyncThunk(
  'user/login',
  async ({ email, password, grecaptcha, rememberMe }, { rejectWithValue }) => {
    try {
      const config = { headers: { 'Content-Type': 'application/json' } };
      const { data } = await axios.post('/api/users/login', { email, password, grecaptcha, rememberMe }, config);
      localStorage.setItem('userInfo', JSON.stringify(data));
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// User Logout
export const logout = createAsyncThunk('user/logout', async (_, { dispatch, getState }) => {
  // Both calls below are independent and already best-effort — run them in
  // parallel rather than paying two sequential round-trips on every logout.
  const state = getState();
  const items = state?.cart?.cartItems || [];
  const ui = state?.user?.userInfo;

  // Persist current cart to server for authenticated users. This is what
  // lets the cart come back on this same account's next login (see
  // fetchServerCart in cartSlice.js) — intentional, not a leftover to
  // remove. The cart is scoped 1:1 to this user, so restoring it is not a
  // cross-user leak. Always sync, even an empty cart — skipping the save
  // when items.length was 0 meant deleting everything and logging out
  // never told the server, so the old (populated) cart came right back on
  // next login.
  const cartSavePromise = (ui && !ui.isGuest)
    ? axios.put('/api/cart', { items }, getAxiosConfig(state))
    : Promise.resolve();

  // Revoke the refresh token server-side so a copied cookie can't keep
  // refreshing a session after the user has logged out.
  const revokePromise = axios.post('/api/users/logout', {}, { withCredentials: true });

  await Promise.allSettled([cartSavePromise, revokePromise]);

  // Clear all user data and cart from localStorage. Guarded like the network
  // calls above — storage can legitimately throw (Safari private browsing,
  // a full quota, a sandboxed iframe), and an unguarded throw here used to
  // stop the thunk before reaching the redirect below, leaving the caller's
  // "logging out" UI state stuck with no way to recover short of a reload.
  try {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('cartItems');
    localStorage.removeItem('shippingAddress');
    localStorage.removeItem('paymentMethod');
    localStorage.removeItem('selectedRate');
  } catch (_) {}

  // Ensure axios Authorization header is cleared on logout
  clearAxiosAuthorization();
  dispatch(resetUserState());
  dispatch({ type: ORDER_LIST_MY_RESET });
  window.location.href = '/login';
});

// User Register
export const register = createAsyncThunk(
  'user/register',
  async ({ firstName, lastName, username, email, password, grecaptcha, acceptedTerms, acceptedPrivacy }, { rejectWithValue }) => {
    try {
      const config = { headers: { 'Content-Type': 'application/json' } };
      const { data } = await axios.post(
        '/api/users',
        { firstName, lastName, username, email, password, grecaptcha, acceptedTerms, acceptedPrivacy },
        config
      );
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// User Details
export const getUserDetails = createAsyncThunk(
  'user/getUserDetails',
  async (id, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state);
  const { data } = await axios.get(`/api/users/${id}`, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// Update User Profile
export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async ({ stepUpToken, ...user }, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
  // Changing email is step-up-gated for non-guest sessions.
  if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
  const { data } = await axios.put('/api/users/profile', user, config);
      localStorage.setItem('userInfo', JSON.stringify(data));
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// Reset Password
export const resetUserPassword = createAsyncThunk(
  'user/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const config = { headers: { 'Content-Type': 'application/json' } };
      const { data } = await axios.post(`/api/users/resetpw/${token}`, { newPassword }, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ stepUpToken, ...user }, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
  // Granting/revoking admin (isAdmin/role fields) is step-up-gated.
  if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
  const { data } = await axios.put(`/api/users/${user._id}`, user, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// Get total users count (admin)
export const getUsersCount = createAsyncThunk(
  'user/getUsersCount',
  async (_, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state);
  const { data } = await axios.get('/api/users/count', config);
      return data.count;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// Update User Password
export const updateUserPassword = createAsyncThunk(
  'user/updateUserPassword',
  async ({ currentPassword, password, id }, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const body = { currentPassword, password };
      if (id) body.id = id; // not used server-side but harmless
      const { data } = await axios.put('/api/users/profile', body, config);
      // Only update stored userInfo if non-guest (token changes after password)
      if (!data.isGuest) {
        localStorage.setItem('userInfo', JSON.stringify(data));
      }
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// Address management thunks
export const fetchAddresses = createAsyncThunk(
  'user/fetchAddresses',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      const { data } = await axios.get('/api/users/addresses', config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const addAddress = createAsyncThunk(
  'user/addAddress',
  async (address, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.post('/api/users/addresses', address, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const updateAddress = createAsyncThunk(
  'user/updateAddress',
  async ({ id, address }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.put(`/api/users/addresses/${id}`, address, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'user/deleteAddress',
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      const { data } = await axios.delete(`/api/users/addresses/${id}`, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const setPreferredAddress = createAsyncThunk(
  'user/setPreferredAddress',
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.put('/api/users/addresses/preferred', { id }, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState: {
    userInfo: null,
    loading: false,
    error: null,
    success: false,
    userDetails: {},
    userList: [],
    userRegister: null,
    userUpdateSuccess: false,
    loadingUpdateUser: false,
    errorUpdateUser: null,
    usersCount: 0,
    loadingUsersCount: false,
    errorUsersCount: null,
    passwordUpdateLoading: false,
    passwordUpdateError: null,
    passwordUpdateSuccess: false,
    // addresses
    shippingAddresses: [],
    preferredShippingAddressId: null,
    addressesLoading: false,
    addressesError: null,
  },
  reducers: {
    resetUserUpdate: (state) => {
      state.userUpdateSuccess = false;
      state.loadingUpdateUser = false;
      state.errorUpdateUser = null;
    },
    resetUserState: (state) => {
      state.userInfo = null;
      state.loading = false;
      state.error = null;
      state.success = false;
      state.userDetails = {};
      state.userRegister = null;
      state.userUpdateSuccess = false;
      state.loadingUpdateUser = false;
      state.errorUpdateUser = null;
      state.usersCount = 0;
      state.loadingUsersCount = false;
      state.errorUsersCount = null;
      state.passwordUpdateLoading = false;
      state.passwordUpdateError = null;
      state.passwordUpdateSuccess = false;
    },
    setUserInfo: (state, action) => {
      state.userInfo = normalizeUser(action.payload)
    },
    // Patches just the access token after a silent refresh (axiosInterceptors.js)
    // — everything else about the session (identity, isAdmin, etc.) is unchanged.
    setAccessToken: (state, action) => {
      if (state.userInfo) {
        state.userInfo = { ...state.userInfo, token: action.payload };
        try { localStorage.setItem('userInfo', JSON.stringify(state.userInfo)); } catch {}
      }
    },
    resetPasswordUpdate: (state) => {
      state.passwordUpdateLoading = false;
      state.passwordUpdateError = null;
      state.passwordUpdateSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Admin Update User
      .addCase(updateUser.pending, (state) => {
        state.loadingUpdateUser = true;
        state.errorUpdateUser = null;
        state.userUpdateSuccess = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loadingUpdateUser = false;
        state.userUpdateSuccess = true;
        // Update details in-place so edit screen reflects latest without refresh
        if (state.userDetails && state.userDetails._id === action.payload?._id) {
          state.userDetails = action.payload;
        }
        // Also update list if present
        const idx = state.userList.findIndex((u) => u._id === action.payload?._id);
        if (idx !== -1) {
          state.userList[idx] = { ...state.userList[idx], ...action.payload };
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loadingUpdateUser = false;
        state.errorUpdateUser = action.payload;
      })

      // Users count
      .addCase(getUsersCount.pending, (state) => {
        state.loadingUsersCount = true;
        state.errorUsersCount = null;
      })
      .addCase(getUsersCount.fulfilled, (state, action) => {
        state.loadingUsersCount = false;
        state.usersCount = action.payload;
      })
      .addCase(getUsersCount.rejected, (state, action) => {
        state.loadingUsersCount = false;
        state.errorUsersCount = action.payload;
      })

      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.userInfo = null;
        state.userDetails = {};
        state.userList = [];
        state.success = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        // Should be unreachable in practice — the thunk swallows its own
        // network errors and guards its storage cleanup — but resetting
        // loading here means an in-flight UI indicator (e.g. Header's
        // disabled Logout item, once it reads this instead of local state)
        // can't get stuck forever if something still throws.
        state.loading = false;
      })

      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.userRegister = action.payload;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // List Users
      .addCase(listUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.userList = action.payload;
        state.error = null;
      })
      .addCase(listUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get User Details
      .addCase(getUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload;
        state.error = null;
      })
      .addCase(getUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.userInfo = action.payload;
        // also refresh userDetails mirror if same user
        if (state.userDetails && state.userDetails._id === action.payload._id){
          state.userDetails = action.payload;
        }
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.userList = state.userList.filter((user) => user._id !== action.payload);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reset Password
      .addCase(resetUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetUserPassword.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update User Password
      .addCase(updateUserPassword.pending, (state) => {
        state.passwordUpdateLoading = true;
        state.passwordUpdateError = null;
        state.passwordUpdateSuccess = false;
      })
      .addCase(updateUserPassword.fulfilled, (state, action) => {
        state.passwordUpdateLoading = false;
        state.passwordUpdateSuccess = true;
        // refresh userInfo (token rotated) but not for guests
        if (!action.payload.isGuest) {
          state.userInfo = action.payload;
        }
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.passwordUpdateLoading = false;
        state.passwordUpdateError = action.payload;
      })

      // Addresses
      .addCase(fetchAddresses.pending, (state) => {
        state.addressesLoading = true;
        state.addressesError = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.addressesLoading = false;
        state.shippingAddresses = action.payload.addresses || [];
        state.preferredShippingAddressId = action.payload.preferredShippingAddressId || null;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.addressesLoading = false;
        state.addressesError = action.payload;
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.shippingAddresses = action.payload.addresses || [];
        state.preferredShippingAddressId = action.payload.preferredShippingAddressId || null;
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.addressesError = action.payload;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.shippingAddresses = action.payload.addresses || [];
        state.preferredShippingAddressId = action.payload.preferredShippingAddressId || null;
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.addressesError = action.payload;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.shippingAddresses = action.payload.addresses || [];
        state.preferredShippingAddressId = action.payload.preferredShippingAddressId || null;
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.addressesError = action.payload;
      })
      .addCase(setPreferredAddress.fulfilled, (state, action) => {
        state.shippingAddresses = action.payload.addresses || [];
        state.preferredShippingAddressId = action.payload.preferredShippingAddressId || null;
      })
      .addCase(setPreferredAddress.rejected, (state, action) => {
        state.addressesError = action.payload;
      });
  },
});

const normalizeUser = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const isGuest = !!raw.isGuest
  // Remove unusable bearer token for guests to avoid guards expecting it
  const sanitized = { ...raw, isGuest }
  if (isGuest) {
    delete sanitized.token
  // Also clear axios Authorization defaults so guest requests don't send Bearer
  clearAxiosAuthorization()
  }
  return sanitized
}

export const { resetUserState, resetUserUpdate, setUserInfo, resetPasswordUpdate, setAccessToken } = userSlice.actions;

export default userSlice.reducer;
